import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, MicIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import { heroPrompts } from '../data/homepage';
import { AtnaConversationDialog } from './demandes/AtnaConversationDialog';
import { InscriptionDialog } from './loisirs/InscriptionDialog';
import { PermisDialog } from './permis/PermisDialog';
import { detectCategory } from '../hooks/useInscriptionConversation';
import { detectPermitType } from '../hooks/usePermisConversation';
import { isBatteryQuestion, isMissedBinReport } from '../hooks/useCollecteConversation';
import { CollecteDialog } from './collectes/CollecteDialog';
import { resetLiveTruck } from '../hooks/useLiveTruck';
const PANEL_LAYOUT_ID = 'atna-hero-panel';
type Flow = 'signalement' | 'inscription' | 'permis' | 'collecte';

/** Choisit le flux ATNA le plus probable à partir de la phrase du citoyen. */
function detectFlow(text: string): Flow {
  const lower = text.toLowerCase();
  // Piles et batteries : question de tri, traitée par l'assistant des collectes.
  if (isBatteryQuestion(lower) || isMissedBinReport(lower)) return 'collecte';
  // Un projet de travaux ou un type de permis reconnu ouvre la demande de permis.
  const wantsPermit = /construir|permis|travaux|rénov|renov|agrandir|installer|abattre|piscine|clôture|cloture/.test(lower) || detectPermitType(lower) !== null;
  if (wantsPermit) return 'permis';
  const wantsRegistration = /inscri|cours|activit|camp|session|natation/.test(lower) || detectCategory(lower) !== null;
  return wantsRegistration ? 'inscription' : 'signalement';
}
export function HeroAtna() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [conversation, setConversation] = useState<{
    flow: Flow;
    text: string;
    direct?: boolean;
  } | null>(null);
  const openConversation = (text: string, flow?: Flow, direct = false) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const resolved = flow ?? detectFlow(trimmed);
    // Bac non ramassé : le camion repart de son point de départ quand la carte apparaît.
    if (resolved === 'collecte' && isMissedBinReport(trimmed)) resetLiveTruck();
    setConversation({
      flow: resolved,
      text: trimmed,
      direct
    });
    setValue('');
  };
  return <section aria-labelledby="hero-atna-titre" className="w-full rounded-3xl border border-white/60 bg-white p-5 shadow-2xl shadow-ville-navy/20 sm:p-6" data-id="element-1716" data-mp="1kurbcs3mxrpi">
      <h2 id="hero-atna-titre" className="sr-only" data-id="element-1717" data-mp="1ax3ik7plau9z">
        Assistant ATNA
      </h2>

      {conversation ?
    // Garde la hauteur pendant que la barre se transforme en dialogue.
    <div className="h-[54px]" aria-hidden="true" data-id="element-1718" data-mp="11x6o1pgir170" /> : <motion.div layoutId={PANEL_LAYOUT_ID} style={{
      borderRadius: 999
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1719" data-mp="11z1bbaf9j5e9">
          <form onSubmit={event => {
        event.preventDefault();
        openConversation(value);
      }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-3 pr-1.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-1720" data-mp="2wb7m4848vk61">
            <button type="button" onClick={() => openConversation(value)} aria-label="Joindre une photo ou un document" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1721" data-mp="vu3h5f1fi3ec">
              <PlusIcon className="h-5 w-5" aria-hidden="true" data-id="element-1722" data-mp="d43tmwl8kwd1" />
            </button>
            <label htmlFor="hero-atna-input" className="sr-only" data-id="element-1723" data-mp="1jkfnilh0zs2t">
              Demander à ATNA
            </label>
            <input id="hero-atna-input" type="text" value={value} onChange={event => setValue(event.target.value)} placeholder="Demander à ATNA…" className="min-w-0 flex-1 border-0 bg-transparent py-2 text-base text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-1724" data-mp="1ohzbwe7ftl8h" />
            <button type="button" aria-label="Dicter votre question" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1725" data-mp="1s16g3ob028di">
              <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-1726" data-mp="2fslnhvhmvfvm" />
            </button>
            <button type="submit" aria-label="Envoyer la question à ATNA" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-lime text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1727" data-mp="2arxy1e91z73i">
              <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-1728" data-mp="38bo0kvdatab7" />
            </button>
          </form>
        </motion.div>}

      <ul className="mt-3 flex flex-wrap gap-2" data-id="element-1729" data-mp="2ufkc7ip3vw6n">
        {heroPrompts.map(prompt => <li key={prompt.label} data-id="element-1730" data-mp="2ygw4jvzd6vij">
            <button type="button" onClick={() => prompt.label === 'Inscrire mon enfant' ? openConversation(prompt.label, 'inscription') : prompt.label === 'Bac non ramassé' ? openConversation(prompt.label, 'collecte') : prompt.label === 'Nid-de-poule' ? openConversation(prompt.label, 'signalement', true) : navigate(prompt.to)} className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1731" data-mp="2kmwhcthos2ak">
              {prompt.label}
            </button>
          </li>)}
      </ul>

      <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-ville-indigo" data-id="element-1732" data-mp="3um7cotempw7w">
        <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1733" data-mp="v6j2smho3f3y" />
        ATNA connaît les services de Beaconsfield
      </p>

      <AnimatePresence data-id="element-1734" data-mp="q8hhduwrr3pb">
        {conversation?.flow === 'signalement' && <AtnaConversationDialog key="atna-hero-dialog" initialText={conversation.text} direct={conversation.direct} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1735" data-mp="m4tbrhrp6i6p" />}
        {conversation?.flow === 'inscription' && <InscriptionDialog key="atna-hero-inscription" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1736" data-mp="31vxplt5brg9w" />}
        {conversation?.flow === 'collecte' && <CollecteDialog key="atna-hero-collecte" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1737" data-mp="1qeuuzqwjncp3" />}
        {conversation?.flow === 'permis' && <PermisDialog key="atna-hero-permis" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1738" data-mp="207jz20prloae" />}
      </AnimatePresence>
    </section>;
}